import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DownloadBrochure = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const currentLang = i18n.language || 'en';
  let buttonWidthClass = 'w-[230px] h-[56px] ml-[-174px]';
  let spanWidthClass = 'w-[154px] opacity-100 mr-2.5';

  if (currentLang === 'sn') {
    buttonWidthClass = 'w-[310px] h-[56px] ml-[-254px]';
    spanWidthClass = 'w-[230px] opacity-100 mr-3';
  } else if (currentLang === 'tn') {
    buttonWidthClass = 'w-[320px] h-[56px] ml-[-264px]';
    spanWidthClass = 'w-[240px] opacity-100 mr-3';
  }

  const toggleVisibility = () => {
    if (window.pageYOffset > 300) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  const [blobUrl, setBlobUrl] = useState(null);

  const basePath = import.meta.env.VITE_BASE_PATH || '';
  const cleanBasePath = basePath.replace(/\/+$/, '');
  const brochureUrl = `${cleanBasePath}/Medica-Brochure.png`;

  // Pre-fetch brochure blob for instant, guaranteed download on click
  useEffect(() => {
    let active = true;
    let createdUrl = null;

    fetch(brochureUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load brochure');
        return res.blob();
      })
      .then((blob) => {
        if (!active) return;
        createdUrl = window.URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch((err) => {
        console.warn('Brochure prefetch error:', err);
      });

    return () => {
      active = false;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [brochureUrl]);

  const handleDownload = (e) => {
    e?.preventDefault();
    setIsDownloading(true);

    // 1. Open the brochure image in a new tab
    window.open(brochureUrl, '_blank', 'noopener,noreferrer');

    // 2. Automatically trigger file download
    const executeDownload = (downloadHref) => {
      const link = document.createElement('a');
      link.href = downloadHref;
      link.download = 'Medica-Brochure.png';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => setIsDownloading(false), 1200);
    };

    if (blobUrl) {
      executeDownload(blobUrl);
    } else {
      fetch(brochureUrl)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch brochure image');
          return res.blob();
        })
        .then((blob) => {
          const freshBlobUrl = window.URL.createObjectURL(blob);
          executeDownload(freshBlobUrl);
          setTimeout(() => window.URL.revokeObjectURL(freshBlobUrl), 5000);
        })
        .catch((err) => {
          console.warn('Download fallback to direct link:', err);
          executeDownload(brochureUrl);
        });
    }
  };

  const shouldShowFull = !isScrolled || isHovered;

  return (
    <button
      onClick={handleDownload}
      className={`z-40 bg-gradient-to-r from-[#1e40af] to-[#2563eb] text-white rounded-full shadow-xl flex items-center justify-center font-semibold text-base overflow-hidden transition-all duration-200 ease-out hover:scale-[1.03] hover:shadow-[0_20px_40px_rgba(37,99,235,0.4)] active:scale-[0.97] cursor-pointer ${
        shouldShowFull ? buttonWidthClass : 'w-[50px] h-[50px] ml-0'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={t('download_brochure_floating_button')}
      title={t('download_brochure_floating_button')}
    >
      <span
        className={`whitespace-nowrap inline-block overflow-hidden transition-all duration-150 ease-out ${
          shouldShowFull ? spanWidthClass : 'w-0 opacity-0 mr-0'
        }`}
      >
        {t('download_brochure_floating_button')}
      </span>

      {/* Download Icon */}
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`flex-shrink-0 transition-transform duration-200 ${isDownloading ? 'animate-bounce' : ''}`}
      >
        <path
          d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points="7 10 12 15 17 10"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          x1="12"
          y1="15"
          x2="12"
          y2="3"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};

export default DownloadBrochure;
